import React, { useState, useEffect } from 'react';
import Form from '../../components/common/Form';
import FormSection from '../../components/common/FormSection';
import FormField from '../../components/common/FormField';
import {
  FirearmLooseBarrelTypeDTO,
  FirearmCalibreDTO,
} from '../../services/apiClient';
import handleChange from '../../utils/formUtils/handleChange';
import { useFirearmCalibres } from '../../hooks/useFirearmCalibreApi';
import {
  useFormValidation,
  ValidationRules,
} from '../../hooks/useFormValidation';

interface FirearmLooseBarrelTypeFormProps {
  initialValues: FirearmLooseBarrelTypeDTO;
  onSubmit: (data: FirearmLooseBarrelTypeDTO) => void;
  onCancel: () => void;
}

const FORM_SECTIONS = {
  basicInfo: {
    id: 'basic-info',
    title: 'Basic Information',
  },
  settings: {
    id: 'settings',
    title: 'Settings',
  },
  calibres: {
    id: 'calibres',
    title: 'Calibres',
  },
} as const;

const FirearmLooseBarrelTypeForm: React.FC<FirearmLooseBarrelTypeFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [formData, setFormData] = useState<FirearmLooseBarrelTypeDTO>(initialValues);
  const { data: calibres = [] } = useFirearmCalibres();
  const [calibreOptions, setCalibreOptions] = useState<{ value: string; label: string }[]>([]);
  const [calibreMap, setCalibreMap] = useState<Map<string, FirearmCalibreDTO>>(new Map());

  const validationRules: ValidationRules = {
    title: [{ required: true, message: 'Title is required' }],
    displayPosition: [
      { required: true, message: 'Position is required' },
      {
        custom: (value) => Number(value) >= 0,
        message: 'Position must be a positive number',
      },
    ],
  };

  const { errors, validate } = useFormValidation<FirearmLooseBarrelTypeDTO>(validationRules);

  useEffect(() => {
    if (calibres.length > 0) {
      const options = calibres
        .filter((c) => c.id)
        .map((c) => ({
          value: c.id as string,
          label: c.title || '',
        }));

      setCalibreOptions(options);
      setCalibreMap(new Map(calibres.map((c) => [c.id as string, c])));
    }
  }, [calibres]);

  useEffect(() => {
    if (calibres.length > 0) {
      const options = calibres
        .filter((c) => c.id)
        .map((c) => ({
          value: c.id as string,
          label: c.title || '',
        }));

      setCalibreOptions(options);
      setCalibreMap(new Map(calibres.map((c) => [c.id as string, c])));
    }
  }, [calibres]);

  const handleSave = () => {
    if (validate(formData)) {
      onSubmit(formData);
    }
  };

  const sections = Object.values(FORM_SECTIONS);

  return (
    <Form sections={sections} onSave={handleSave} onCancel={onCancel}>
      <FormSection
        id={FORM_SECTIONS.basicInfo.id}
        title={FORM_SECTIONS.basicInfo.title}
      >
        <FormField
          id="title"
          label="Title"
          type="text"
          value={formData.title || ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Title"
          required
          error={errors.title}
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.settings.id}
        title={FORM_SECTIONS.settings.title}
      >
        <FormField
          id="notOnList"
          label="Not On List?"
          type="checkbox"
          value={formData.notOnList || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to show not on list option."
        />
        <FormField
          id="displayPosition"
          label="Position"
          type="number"
          value={formData.displayPosition ?? ''}
          onChange={(e) => handleChange(e, setFormData)}
          placeholder="Enter Display Position"
          helper="Lower numbers will be displayed first."
          required
          error={errors.displayPosition}
        />
        <FormField
          id="isActive"
          label="Active?"
          type="checkbox"
          value={formData.isActive || ''}
          onChange={(e) => handleChange(e, setFormData)}
          helper="Check to make record active."
        />
      </FormSection>

      <FormSection
        id={FORM_SECTIONS.calibres.id}
        title={FORM_SECTIONS.calibres.title}
      >
        <FormField
          id="calibres"
          label="Calibres"
          type="multiselect"
          value={(formData.calibres || []).map((c) => c.id as string)}
          onChange={(e) => {
            const selectedIds = Array.isArray(e.target.value)
              ? e.target.value
              : [e.target.value];

            const selectedCalibres = selectedIds
              .map((id) => calibreMap.get(id))
              .filter((c): c is FirearmCalibreDTO => c !== undefined);

            handleChange(
              {
                target: {
                  id: 'calibres',
                  value: selectedCalibres,
                },
              } as any,
              setFormData,
            );
          }}
          options={calibreOptions}
          placeholder="Select calibres..."
          helper="Select one or more calibres for this loose barrel type"
        />
      </FormSection>
    </Form>
  );
};

export default FirearmLooseBarrelTypeForm;
